import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function AdminBoostsTab() {
  const [cfg, setCfg] = useState(null);
  const [perDay, setPerDay] = useState("50");
  const [boosts, setBoosts] = useState([]);
  const [savingCfg, setSavingCfg] = useState(false);
  const load = async () => {
    const [c, b] = await Promise.all([
      api.get("/boost-config"),
      api.get("/admin/boosts"),
    ]);
    setCfg(c.data); setPerDay(String(c.data.cost_per_day));
    setBoosts(b.data || []);
  };
  useEffect(() => { load(); }, []);

  const saveCfg = async () => {
    const v = Number(perDay);
    if (!Number.isFinite(v) || v < 0) return toast.error("Cost per day must be a non-negative number.");
    setSavingCfg(true);
    try {
      await api.patch("/admin/boost-config", { cost_per_day: v });
      toast.success(`Boost cost updated to ₹${v}/day.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
    finally { setSavingCfg(false); }
  };
  const endBoost = async (id) => {
    if (!window.confirm("End this boost now? Unused days will be refunded to the vendor's wallet.")) return;
    try {
      const { data } = await api.post(`/admin/boosts/${id}/end`);
      toast.success(`Boost ended. Refunded ₹${data.refund_amount.toLocaleString("en-IN")} for ${data.days_refunded} unused day${data.days_refunded === 1 ? "" : "s"}.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not end boost"); }
  };
  const now = new Date();
  const isActive = (b) => b.status === "active" && new Date(b.start_date) <= now && new Date(b.end_date) >= now;
  const active = boosts.filter(isActive);
  const others = boosts.filter((b) => !isActive(b));
  const fmt = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div>
      <div className="p-6 bg-white rounded-2xl border border-gray-200 max-w-lg mb-8" data-testid="boost-settings-card">
        <div className="font-display text-xl mb-1">Boost Settings</div>
        <p className="text-xs text-gray-500 mb-3">Cost charged to a vendor's wallet for each day a product is boosted. Changes apply to new boosts only — active boosts keep the price they were purchased at.</p>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1">Cost per day (₹)</div>
            <Input data-testid="boost-cost-input" type="number" min="0" step="1" value={perDay} onChange={(e) => setPerDay(e.target.value)} />
          </div>
          <Button data-testid="boost-cost-save" onClick={saveCfg} disabled={savingCfg} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]">
            {savingCfg ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6 max-w-lg">
        <div className="p-4 rounded-2xl bg-white border border-gray-200" data-testid="boost-total-revenue">
          <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1">Total boost revenue</div>
          <div className="font-display text-2xl">₹{boosts.reduce((sum, b) => sum + Number(b.total_cost || 0) - Number(b.refund_amount || 0), 0).toLocaleString("en-IN")}</div>
        </div>
        <div className="p-4 rounded-2xl bg-white border border-gray-200">
          <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1">Active boosts</div>
          <div className="font-display text-2xl">{active.length}</div>
        </div>
      </div>

      <div className="mb-4 flex items-baseline justify-between">
        <div className="font-display text-xl">Active boosts ({active.length})</div>
      </div>
      {active.length === 0 ? (
        <div className="p-8 bg-white border border-gray-200 rounded-2xl text-center text-gray-500 text-sm mb-8">No active boosts right now.</div>
      ) : (
        <div className="overflow-x-auto bg-white border border-gray-200 rounded-2xl mb-8">
          <table className="w-full text-sm" data-testid="active-boosts-table">
            <thead className="text-xs uppercase tracking-widest text-gray-500 bg-gray-50">
              <tr>
                <th className="text-left p-3">Vendor</th>
                <th className="text-left p-3">Product</th>
                <th className="text-left p-3">Dates</th>
                <th className="text-right p-3">Days</th>
                <th className="text-right p-3">Paid</th>
                <th className="text-right p-3"></th>
              </tr>
            </thead>
            <tbody>
              {active.map((b) => (
                <tr key={b.id} className="border-t border-gray-100" data-testid={`boost-row-${b.id}`}>
                  <td className="p-3">{b.vendor_business_name || b.vendor_id.slice(0, 8)}</td>
                  <td className="p-3">{b.product_name}</td>
                  <td className="p-3">{fmt(b.start_date)} → {fmt(b.end_date)}</td>
                  <td className="p-3 text-right font-mono-tab">{b.days}</td>
                  <td className="p-3 text-right font-mono-tab">₹{Number(b.total_cost).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right">
                    {b.auto_renew && <span className="mr-2 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#FEF3E6] text-[#F48E2E] font-bold">Auto renew</span>}
                    <Button
                      data-testid={`boost-end-${b.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => endBoost(b.id)}
                      className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
                    >End early</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mb-3 font-display text-xl">History ({others.length})</div>
      {others.length === 0 ? (
        <div className="p-8 bg-white border border-gray-200 rounded-2xl text-center text-gray-500 text-sm">No past boosts yet.</div>
      ) : (
        <div className="overflow-x-auto bg-white border border-gray-200 rounded-2xl">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-gray-500 bg-gray-50">
              <tr>
                <th className="text-left p-3">Vendor</th>
                <th className="text-left p-3">Product</th>
                <th className="text-left p-3">Dates</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Paid</th>
                <th className="text-right p-3">Refunded</th>
              </tr>
            </thead>
            <tbody>
              {others.map((b) => (
                <tr key={b.id} className="border-t border-gray-100">
                  <td className="p-3">{b.vendor_business_name || b.vendor_id.slice(0, 8)}</td>
                  <td className="p-3">{b.product_name}</td>
                  <td className="p-3">{fmt(b.start_date)} → {fmt(b.end_date)}</td>
                  <td className="p-3">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${b.status === "expired" ? "bg-gray-100 text-gray-600" : "bg-amber-100 text-amber-700"}`}>
                      {b.status === "ended_early" ? "Ended early" : b.status}
                    </span>
                  </td>
                  <td className="p-3 text-right font-mono-tab">₹{Number(b.total_cost).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right font-mono-tab">{b.refund_amount ? `₹${Number(b.refund_amount).toLocaleString("en-IN")}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
